import React, { useState } from 'react';
import { Container, ErrorMessageBox, FormList, FormListButtonBox, FormListRow, MetadataBox } from './styles';
import { Field, Form, Formik } from 'formik';
import UploadImage from 'components/UploadImage/field';
import SelectComponent from 'components/Select';
import {
  toursSeturInitialValues,
  toursSeturValidationSchema,
  TourSeturFormProps,
  states,
  touristicExperiencesTypes,
  toursTypes,
} from './utils';
import Button from 'components/Button';
import FieldMask from 'components/FieldMask';
import ErrorMessage from 'components/ErrorMessage';
import TextArea from 'components/TextArea';
import LimitedField from 'components/LimitedField';
import { useHistory } from 'react-router-dom';
import { useLoading } from 'hooks/useLoading';
import TouristOffersBusinessService from 'services/TouristOffersBusinessService';
import ImageService from 'services/ImageService';
import MessageModal from 'common/MessageModal';
import ConfirmationModal from 'components/ConfirmationModal';
import Tooltip from 'components/Tooltip';
import questionMarkIcon from 'assets/images/questionMarkIcon.svg';
import { placeIDLink } from 'utils/placeIDLink';

import ValidationWarningModal from 'components/ValidationWarningModal';
import { checkBusinessWarnings } from 'utils/formWarnings';

const TourSeturForm = ({ selectedId, item }: TourSeturFormProps): JSX.Element => {
  const { openLoading, closeLoading } = useLoading();
  const history = useHistory();
  const [messageInfo, setMessageInfo] = useState({ title: '', message: '' });
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [formWarnings, setFormWarnings] = useState<string[]>([]);

  const service = new TouristOffersBusinessService();
  const imageService = new ImageService();

  const onSubmit = async (values: any) => {
    openLoading();

    try {
      const [images, image] = await Promise.all([
        values.images?.length ? imageService.uploadMultiple(values.images) : [],
        values.image?.length ? imageService.uploadMultiple(values.image) : [],
      ]);

      const restoredImages = (images || []).map((image: string) => ({
        image,
        description: '',
      }));

      const restoredObject = {
        ...values,
        images: [...restoredImages],
        image: image?.[0] || '',
      };

      selectedId && selectedId.length > 0
        ? await service.update(Number(selectedId), restoredObject)
        : await service.create({
            ...restoredObject,
            type: 'Passeio e Lazer',
          });

      setMessageInfo({ title: 'Sucesso', message: 'Dados salvos com sucesso' });
      setShowModal(true);
    } catch (error) {
      setMessageInfo({ title: 'Erro', message: 'Erro ao salvar os dados' });
      setShowModal(true);
    }
    closeLoading();
  };

  const closeModal = () => {
    setShowModal(false);

    if (messageInfo.title === 'Sucesso') {
      history.push('/admin/config/activities');
    }
  };

  return (
    <Container>
      {item && (item.createdAt || item.updatedAt || item.user) && (
        <MetadataBox>
          {item.createdAt && (
            <span>
              <strong>Cadastrado em:</strong> {new Date(item.createdAt).toLocaleString('pt-BR')}
            </span>
          )}
          {item.updatedAt && (
            <span>
              <strong>Última Atualização:</strong> {new Date(item.updatedAt).toLocaleString('pt-BR')}
            </span>
          )}
          {item.user && (
            <span>
              <strong>Por:</strong> {typeof item.user === 'object' ? item.user.name || item.user.email : item.user}
            </span>
          )}
        </MetadataBox>
      )}
      <Formik
        initialValues={{ ...toursSeturInitialValues, ...item }}
        validationSchema={toursSeturValidationSchema}
        onSubmit={values => {
          const warnings = checkBusinessWarnings(values);
          if (warnings.length > 0) {
            setFormWarnings(warnings);
            setShowWarningModal(true);
          } else {
            setShowConfirmModal(true);
          }
        }}
        enableReinitialize>
        {formik => (
          <Form>
            <FormList>
              <FormListRow>
                <SelectComponent
                  name="experiences"
                  options={touristicExperiencesTypes}
                  label="Experiência Turística"
                  onChange={value => formik.setFieldValue('experiences', value)}
                  withCheckbox
                  selected={formik.values.experiences?.length > 0 ? formik.values.experiences : []}
                />
                <ErrorMessageBox>
                  <ErrorMessage name="experiences" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <SelectComponent
                  name="businessType"
                  options={toursTypes}
                  label="Classificação"
                  onChange={value => formik.setFieldValue('businessType', value)}
                  selected={formik.values.businessType ? [formik.values.businessType] : []}
                />
                <ErrorMessageBox>
                  <ErrorMessage name="businessType" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <LimitedField id="tradeName" name="tradeName" label="Nome Fantasia" maxLength={255} />
                <ErrorMessageBox className="with-limited-field">
                  <ErrorMessage name="tradeName" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <LimitedField
                  id="title"
                  name="title"
                  label={
                    <span style={{ display: 'flex', alignItems: 'center', color: '#272727' }}>
                      Título da Atividade
                      <Tooltip text="Campo referente ao título que aparecerá no card para visualização do turista." />
                    </span>
                  }
                  maxLength={255}
                />
                <ErrorMessageBox className="with-limited-field">
                  <ErrorMessage name="title" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="document">CNPJ</label>
                <FieldMask type="cnpj">
                  <Field id="document" name="document" type="text" />
                </FieldMask>
                <ErrorMessageBox>
                  <ErrorMessage name="document" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="address.zipCode">CEP</label>
                <FieldMask type="zipcode">
                  <Field id="address.zipCode" name="address.zipCode" type="text" />
                </FieldMask>
                <ErrorMessageBox>
                  <ErrorMessage name="address.zipCode" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow className="col-2">
                <LimitedField id="address.street" name="address.street" label="Endereço" maxLength={255} />
                <ErrorMessageBox className="with-limited-field">
                  <ErrorMessage name="address.street" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="address.number">Número</label>
                <Field id="address.number" name="address.number" type="text" />
                <ErrorMessageBox>
                  <ErrorMessage name="address.number" />
                </ErrorMessageBox>
              </FormListRow>

              <FormListRow>
                <LimitedField
                  id="address.additionalData"
                  name="address.additionalData"
                  label="Complemento"
                  maxLength={255}
                />
                <ErrorMessageBox className="with-limited-field">
                  <ErrorMessage name="address.additionalData" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <LimitedField id="address.neighborhood" name="address.neighborhood" label="Bairro" maxLength={255} />
                <ErrorMessageBox className="with-limited-field">
                  <ErrorMessage name="address.neighborhood" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <LimitedField id="address.city" name="address.city" label="Cidade" maxLength={255} />
                <ErrorMessageBox className="with-limited-field">
                  <ErrorMessage name="address.city" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <SelectComponent
                  name="address.state"
                  options={states}
                  label="Estado"
                  onChange={value => formik.setFieldValue('address.state', value)}
                  selected={formik.values.address.state ? [formik.values.address.state] : []}
                />
                <ErrorMessageBox>
                  <ErrorMessage name="address.state" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="googleMapsLink" style={{ display: 'flex', alignItems: 'center' }}>
                  Place ID do Google Maps
                  <a
                    href={placeIDLink}
                    target="_blank"
                    rel="noreferrer"
                    title="Clique aqui para baixar o PDF de instruções sobre o PlaceID do Google Maps">
                    <img src={questionMarkIcon} alt="Mais informações" />
                  </a>
                </label>
                <Field id="googleMapsLink" name="googleMapsLink" type="text" />
                <ErrorMessageBox>
                  <ErrorMessage name="googleMapsLink" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow className="col-4">
                <TextArea maxLength={750} name="description" title="Descrição" />
                <ErrorMessageBox className="for-textarea">
                  <ErrorMessage name="description" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow className="col-4">
                <TextArea
                  maxLength={750}
                  name="tour.attractions"
                  title="Observações"
                  hasQuestionMark
                  questionMarkText="Preencha esse campo com regras e avisos sobre o passeio para os turistas. Ex.: É necessário roupa de banho"
                />
                <ErrorMessageBox className="for-textarea">
                  <ErrorMessage name="tour.attractions" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="phone">Telefone</label>
                <FieldMask type="phone" id="phone">
                  <Field id="phone" name="phone" type="text" />
                </FieldMask>
                <ErrorMessageBox>
                  <ErrorMessage name="phone" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="whatsapp">WhatsApp</label>
                <FieldMask type="phone" id="whatsapp">
                  <Field id="whatsapp" name="whatsapp" type="text" />
                </FieldMask>
                <ErrorMessageBox>
                  <ErrorMessage name="whatsapp" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow className="col-2">
                <label htmlFor="tradeEmail">E-mail Comercial</label>
                <Field id="tradeEmail" name="tradeEmail" type="text" />
                <ErrorMessageBox>
                  <ErrorMessage name="tradeEmail" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="youtubeLink">Link de Vídeo do YouTube</label>
                <Field id="youtubeLink" name="youtubeLink" type="text" />
                <ErrorMessageBox>
                  <ErrorMessage name="youtubeLink" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="site">Link do Site</label>
                <Field id="site" name="site" type="text" />
                <ErrorMessageBox>
                  <ErrorMessage name="site" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="instagram-link">Link do Instagram</label>
                <Field id="instagramLink" name="instagramLink" type="text" />
                <ErrorMessageBox>
                  <ErrorMessage name="instagramLink" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="facebookLink">Link do Facebook</label>
                <Field id="facebookLink" name="facebookLink" type="text" />
                <ErrorMessageBox>
                  <ErrorMessage name="facebookLink" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow className="col-4">
                <UploadImage
                  descriptionText={`Imagem do Card: Adicione 1 imagem. Para garantir a qualidade da visualização, as imagens precisam ter dimensão mínima de 500 x 600 pixels`}
                  multipleFiles={false}
                  limitFiles={1}
                  htmlFor="image"
                  name="image"
                />
                <ErrorMessageBox className="for-uploadimages">
                  <ErrorMessage name="image" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow className="col-4">
                <UploadImage
                  descriptionText="Imagens da Biblioteca: Adicione até 12 imagens. Para garantir a qualidade da visualização, as imagens precisam ter dimensão mínima de 572 x 432 pixels"
                  multipleFiles
                  limitFiles={12}
                  htmlFor="images"
                  name="images"
                />
                <ErrorMessageBox className="for-uploadimages">
                  <ErrorMessage name="images" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow className="col-4">
                <FormListButtonBox>
                  <Button type="button" buttonType="secondary" onClick={() => history.push('/admin/config/activities')}>
                    Cancelar
                  </Button>
                  <Button type="submit">{selectedId && selectedId.length > 0 ? 'Salvar' : 'Criar'}</Button>
                </FormListButtonBox>
              </FormListRow>
            </FormList>
            <ConfirmationModal
              message="Deseja Continuar?"
              title={`CONFIRMAÇÃO DE ${
                selectedId && selectedId.length > 0 ? 'EDIÇÃO DE NEGÓCIO' : 'CRIAÇÃO DE NEGÓCIO'
              }`}
              onConfirm={() => {
                onSubmit(formik.values);
                setShowConfirmModal(false);
              }}
              onCancel={() => setShowConfirmModal(false)}
              confirmText="Enviar"
              cancelText="Cancelar"
              open={showConfirmModal}
              type="submit"
            />
            <ValidationWarningModal
              open={showWarningModal}
              warnings={formWarnings}
              onConfirm={() => {
                setShowWarningModal(false);
                onSubmit(formik.values);
              }}
              onCancel={() => setShowWarningModal(false)}
            />
          </Form>
        )}
      </Formik>
      <MessageModal isOpen={showModal} title={messageInfo.title} message={messageInfo.message} onClose={closeModal} />
    </Container>
  );
};

export default TourSeturForm;
